/** @file App.tsx
 *  @brief  Created on: May 21, 2024
 *          Davide Scalisi
 *
 * @copyright [2024] Davide Scalisi *
 * @copyright All Rights Reserved. *
 *
*/

/************************************************************************************************************
* Imports
************************************************************************************************************/

import React, { useState, useEffect } from 'react';
import './App.css';

import profile_picture from "./pictures/pp.png";
import logo_linkedin from "./pictures/LinkedIn.svg";
import logo_telegram from "./pictures/Telegram.svg";
import logo_github from "./pictures/GitHub.svg";
import logo_mail from "./pictures/Mail.svg";

import descriptions from "./descriptions.json";

/************************************************************************************************************
* Interfaces
************************************************************************************************************/

interface JSONProject {
	date:			string,
	title:		string,
	brief:		string,
	ref:			string,
	pictures:	Array<string>
}

interface JSONSection {
	title:		string,
	brief:		string,
	projects:	Array<JSONProject>,
}

interface Contact {
	logo:	string,
	name:	string,
	url:	string
}

/************************************************************************************************************
* Private Variables
************************************************************************************************************/

const section_projects_university = descriptions.sections[0] as JSONSection;
const section_projects_personal = descriptions.sections[1] as JSONSection;

/************************************************************************************************************
* Private Functions
************************************************************************************************************/

/**
 * Given a text with `\n` chars, returns its react formatted component.
 * @param text The input text.
 * @returns The formatted react component.
 */
function __expand_text(text: string): React.ReactNode {
	return text
		.split("\n")
		.map(row => <>{row}<br/><br/></>)
		.reduce((acc, cur) => <>{acc}{cur}</>);
}

/************************************************************************************************************
* React components and respective props
************************************************************************************************************/

interface HeaderProps extends FooterProps {
	brief: string
}

function Header({name, brief}: HeaderProps){
	return (
		<div className="header">
			<div className="container">
				<div className="profile-picture">
					<img src={profile_picture} alt="Profile" className="round-image" />
				</div>
				<div>
					<h1>{name}</h1>
					{brief}
				</div>
			</div>
		</div>
	);
}

interface NavbarProps {
	idList: Array<string>
}

function Navbar({ idList }: NavbarProps){
	const linkList = idList
		.map(e => <a href={"#" + e}>{e[0].toUpperCase() + e.slice(1).toLocaleLowerCase()}</a>)
		.reduce((acc, cur) => <>{acc}{cur}</>);

	return (
		<div className="navbar">
			{linkList}
		</div>
	);
}

interface SectionProps {
	id?:				string,
	title:			string,
	date?:			string,
	children?:	React.ReactNode		// The content of the tag (<Section ...> ImTheChildrenProp </Section>).
}

function Section({ id, title, date, children }: SectionProps){
	return (
		<div id={id ? id : ""} className="section">
			<div className="container">
				{
					date ?
						<>
							<h2>{date}</h2>
							<h3>{title}</h3>
						</> :
						<h2>{title}</h2>
				}
				{children}
			</div>
		</div>
	);
}

interface ProjectProps {
	project:		JSONProject,
	children?:	React.ReactNode
}

function Project({ project, children }: ProjectProps){
	const {date, title, ref, pictures} = project;
	const brief = __expand_text(project.brief);
	const pictureList = pictures
		.map(e => {
			// eslint-disable-next-line @typescript-eslint/no-var-requires
			const picture = require("./pictures/projects/" + e + ".png");

			return (
				<a href={picture}>
					<img src={picture} className="project-picture" />
				</a>
			);
		})
		.reduce((acc, cur) => <>{acc}{cur}</>);

	return (
		<Section date={date} title={title}>
			<div className="project">
				<div className="project-pictures">{pictureList}</div>
				{brief}
				{children}
			</div>
			<p className="project-reference-p">
				<a href={ref} className="project-reference"> Documentation </a>
			</p>
		</Section>
	);
}

interface ContactProps extends SectionProps {
	contacts: Array<Contact>
}

function Contacts({ id, title, children, contacts }: ContactProps){
	const contactList = contacts.map(e =>
		<li>
			<img src={e.logo} alt={e.name} className="contact-logo" />
			<a href={e.url}>{e.name}</a>
		</li>
	)
	.reduce((acc, cur) => <>{acc}{cur}</>);

	return (
		<Section id={id} title={title}>
			<ul>{contactList}</ul>
			{children}
		</Section>
	);
}

function ScrollToTopButton(){
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			if (window.pageYOffset > 100){
				setIsVisible(true);
			} else{
				setIsVisible(false);
			}
		};

		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const scrollToTop = () => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	};

	const buttonClass = `scroll-to-top ${isVisible ? 'visible' : ''}`;

	return (
		<button className={buttonClass} onClick={scrollToTop}>
			↑
		</button>
	);
}

interface FooterProps {
	name: string
}

function Footer({name}: FooterProps){
	return (
		<div className="footer">
			<div className="container">
				<p>&copy;&nbsp;{new Date().getFullYear()}&nbsp;{name}. All rights reserved.</p>
			</div>
		</div>
	);
}

/**
 * Main react component.
 */
function App(){
	return (
		<div className="App">
			<Header name="Davide Scalisi" brief="Embedded C/C++ Firmware Engineer" />
			<Navbar idList={["about", "projects", "contacts"]} />

				<Section id="about" title="About Me">
					{__expand_text(descriptions.about)}
				</Section>

			<span id="projects">
				<Section id="projects_personal" title={section_projects_personal.title}>
				{section_projects_personal.brief}
				{
					section_projects_personal.projects
						.map(project => <Project project={project}></Project>)
						.reduce((acc, cur) => <>{acc}{cur}</>)
				}
				</Section>

				<Section id="projects_university" title={section_projects_university.title}>
				{section_projects_university.brief}
				{
					section_projects_university.projects
						.map(project => <Project project={project}></Project>)
						.reduce((acc, cur) => <>{acc}{cur}</>)
				}
				</Section>
			</span>

			<Contacts id="contacts" title="Contact Me" contacts={[
				{ logo: logo_github, name: "GitHub", url: "https://github.com/LoZioo" },
				{ logo: logo_linkedin, name: "LinkedIn", url: "https://www.linkedin.com/in/lozio" },
				{ logo: logo_telegram, name: "Telegram", url: "https://t.me/Davide_Scaliz" },
				{ logo: logo_mail, name: "Mail", url: "mailto:sc.dada58@gmail.com" }
			]}>
				<b> Please feel free to contact me! </b>
			</Contacts>

			<Footer name="Davide Scalisi" />
			<ScrollToTopButton />
		</div>
	);
}

export default App;
